import { Box, Chip, Divider, Typography } from '@mui/material';
import { FC } from 'react';
import { Duties } from 'components/Order/OrderDetails/OrderDetailsRow/OrderDetailsRowChild/Duties';
import { Total } from 'components/Order/OrderDetails/OrderDetailsRow/OrderDetailsRowChild/Total';
import { representationSchemeType } from 'services/constants/representationSchemeType';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { toNumber } from 'utils/toNumber';
import { TRefundBreakdownItemProps } from './types';
import { COLORS } from 'services/constants/colors';

export const RefundBreakdownItem: FC<TRefundBreakdownItemProps> = ({
	refundBreakdownItem,
	detalization,
	paymentCurrency,
}) => {
	const itemAmount = toNumber(refundBreakdownItem.returnItemAmount);
	const itemQuantity = toNumber(refundBreakdownItem.returnItemQuantity);
	const vatValue = toNumber(refundBreakdownItem.returnVatValue);
	const deliveryCost = toNumber(refundBreakdownItem.returnDeliveryCost);
	const vatOnDeliveryCharge = toNumber(refundBreakdownItem.returnVatOnDeliveryCharge);
	const easFee = toNumber(refundBreakdownItem.returnEasFee);
	const vatOnEasFee = toNumber(refundBreakdownItem.returnVatOnEasFee);

	const totalCost =
		itemAmount + vatValue + easFee + vatOnEasFee + deliveryCost + vatOnDeliveryCharge;

	return (
		<Box sx={{ ml: 2 }}>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'space-between',
					gap: 2,
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'row',
						gap: 1,
					}}
				>
					<Typography
						variant="body2"
						sx={{
							fontWeight: 'bold',
						}}
						color="primary"
					>
						{representationSchemeType?.[
							refundBreakdownItem.repScheme as keyof typeof representationSchemeType
						] ?? refundBreakdownItem.repScheme}
					</Typography>
					<Typography variant="body2">
						<span style={{ fontWeight: 'bold' }}>{refundBreakdownItem.idProvidedByEm} — </span>
						{refundBreakdownItem.shortDescription}
					</Typography>
					<Chip
						label={`${itemQuantity} returned`}
						size="small"
						sx={{
							p: '0 2px',
							fontSize: '.7rem',
							height: '20px',
							bgcolor: COLORS.RETURNED,
						}}
					/>
				</Box>

				{detalization ? (
					<Box
						sx={{
							display: 'flex',
							gap: 1,
						}}
					>
						<Typography
							variant="body2"
							sx={{
								fontSize: '.8rem',
							}}
							color="text.secondary"
						>
							<AmountAndCurrency
								amount={itemQuantity ? itemAmount / itemQuantity : itemAmount}
								currency={paymentCurrency || 'EUR'}
								bold
								size={0.8}
								color="text.secondary"
							/>
							&nbsp;&times;&nbsp;{itemQuantity}&nbsp;=&nbsp;
						</Typography>
						<AmountAndCurrency
							amount={itemAmount}
							currency={paymentCurrency || 'EUR'}
							bold
							size={0.9}
							color="text.secondary"
						/>
					</Box>
				) : (
					<AmountAndCurrency amount={totalCost} currency={paymentCurrency || 'EUR'} bold size={1} />
				)}
			</Box>
			{detalization && (
				<>
					<Duties duty={vatValue} nameOfDuty="Item VAT" paymentCurrency={paymentCurrency} />
					{!!deliveryCost && (
						<Duties
							duty={deliveryCost}
							nameOfDuty="Delivery Charge"
							paymentCurrency={paymentCurrency}
						/>
					)}
					{!!vatOnDeliveryCharge && (
						<Duties
							duty={vatOnDeliveryCharge}
							nameOfDuty="VAT on Delivery Charge"
							paymentCurrency={paymentCurrency}
						/>
					)}
					{!!easFee && (
						<Duties duty={easFee} nameOfDuty="EAS Fee" paymentCurrency={paymentCurrency} />
					)}
					{!!vatOnEasFee && (
						<Duties
							duty={vatOnEasFee}
							nameOfDuty="VAT on EAS Fee (22%)"
							paymentCurrency={paymentCurrency}
						/>
					)}

					<Divider sx={{ mt: 0.4, mb: 0.4, ml: 4 }} />
					<Total
						totalPerItem={itemAmount}
						quantity={itemQuantity}
						totalCost={totalCost}
						paymentCurrency={paymentCurrency}
					/>
				</>
			)}
		</Box>
	);
};
